import React from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCartContext } from '../../../context/CartContext';
import { useRestaurantContext } from '../../../context/RestaurantContext';
import MoneyUtils from '../../../utils/moneyUtils';
import { capitalizeFirst } from '../../../utils/stringUtils';
import Icon from '../../../components/AppIcon';

// ─── Helper: build a short customization summary string ──────────────────────
const buildCustomizationSummary = (cartItem) => {
  const cust = cartItem.customizations;
  if (!cust) return null;

  const parts = [];
  if (cust.portion) {
    parts.push(capitalizeFirst(cust.portion.name || cust.portion.size || ''));
  }
  const addons = cust.addons || cust.addOns || [];
  if (addons.length > 0) {
    const addonStr = addons
      .filter((a) => (a.quantity || 1) > 0)
      .map((a) => {
        const qty = a.quantity && a.quantity > 1 ? `${a.quantity}x ` : '';
        return `${qty}${capitalizeFirst(a.name)}`;
      })
      .join(', ');
    if (addonStr) parts.push(addonStr);
  }
  if (cartItem.specialInstructions) {
    parts.push(`"${cartItem.specialInstructions}"`);
  }
  return parts.length > 0 ? parts.join(' + ') : null;
};

// ─── Single cart entry row ────────────────────────────────────────────────────
const CartEntryRow = ({ cartItem, themeColor, themeTextColor, onEdit }) => {
  const { updateQuantity, removeItem } = useCartContext();

  const addonCost = (cartItem.customizations?.addons || cartItem.customizations?.addOns || []).reduce(
    (s, a) => s + (a.price || 0) * (a.quantity || 1),
    0
  );
  const unitTotal = (cartItem.price || 0) + addonCost;
  const rowTotal = unitTotal * (cartItem.quantity || 1);
  const summary = buildCustomizationSummary(cartItem);

  const handleDecrement = async () => {
    if (cartItem.quantity <= 1) {
      await removeItem(cartItem.cartItemId);
    } else {
      await updateQuantity(cartItem.cartItemId, cartItem.quantity - 1);
    }
  };

  const handleIncrement = async () => {
    await updateQuantity(cartItem.cartItemId, cartItem.quantity + 1);
  };

  return (
    <div className="px-4 py-3.5 border-b border-gray-100 last:border-b-0">
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center mt-0.5"
          style={{ backgroundColor: `${themeColor}12` }}
        >
          {cartItem.imageUrl ? (
            <img
              src={cartItem.imageUrl}
              alt={cartItem.name}
              className="w-full h-full object-cover rounded-xl"
            />
          ) : (
            <Icon
              name={
                /(drink|beverage|juice|coffee|tea|shake|smoothie|water)/i.test(
                  cartItem.category || cartItem.name
                )
                  ? 'Coffee'
                  : 'Utensils'
              }
              size={16}
              color={themeColor}
              strokeWidth={2}
            />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-gray-900 leading-snug">
                {capitalizeFirst(cartItem.name)}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {MoneyUtils.format(unitTotal)}
              </p>
              {summary && (
                <p className="text-[11px] text-gray-400 mt-0.5 leading-snug line-clamp-2">
                  {summary}
                </p>
              )}
            </div>

            {/* Right: total + stepper */}
            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
              <p className="text-sm font-black text-gray-900">
                {MoneyUtils.format(rowTotal)}
              </p>
              <div
                className="flex items-center gap-2 rounded-full border px-2 py-1 bg-white shadow-sm"
                style={{ borderColor: themeColor }}
              >
                <button
                  onClick={handleDecrement}
                  className="w-5 h-5 flex items-center justify-center font-bold text-base leading-none"
                  style={{ color: themeColor }}
                >
                  -
                </button>
                <span
                  className="text-sm font-black w-4 text-center leading-none"
                  style={{ color: themeColor }}
                >
                  {cartItem.quantity}
                </span>
                <button
                  onClick={handleIncrement}
                  className="w-5 h-5 flex items-center justify-center rounded-full font-bold text-base leading-none text-white"
                  style={{ backgroundColor: themeColor }}
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {/* Edit link */}
          {onEdit && (
            <button
              onClick={() => onEdit(cartItem)}
              className="mt-1.5 flex items-center gap-1 text-[11px] font-bold"
              style={{ color: themeColor }}
            >
              Edit
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main ItemRepeatSheet ─────────────────────────────────────────────────────
const ItemRepeatSheet = ({ isOpen, item, onClose, onAddNew, onEdit }) => {
  const { getItemsInCartById } = useCartContext();
  const { theme } = useRestaurantContext();

  const themeColor = theme?.secondary || '#000';
  const themeTextColor = theme?.primary || '#fff';

  const cartEntries = item ? getItemsInCartById(item.id || item._id) : [];

  const handleDragEnd = (_, info) => {
    if (info.offset.y > 100 || info.velocity.y > 500) {
      onClose();
    }
  };

  const sheet = (
    <AnimatePresence>
      {isOpen && item && (
        <motion.div
          className="fixed inset-0 z-[80] flex justify-center items-end"
          style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-t-2xl w-full max-w-md shadow-2xl relative overflow-hidden"
            drag="y"
            dragConstraints={{ top: 0 }}
            dragElastic={0.1}
            onDragEnd={handleDragEnd}
            onClick={(e) => e.stopPropagation()}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>

            {/* Header */}
            <div className="px-4 pb-3 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-base font-black text-gray-900">
                  {capitalizeFirst(item.name)}
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {cartEntries.length} option{cartEntries.length !== 1 ? 's' : ''} in your cart
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Cart Entries */}
            <div className="max-h-[55vh] overflow-y-auto [&::-webkit-scrollbar]:hidden">
              {cartEntries.length > 0 ? (
                cartEntries.map((entry) => (
                  <CartEntryRow
                    key={entry.cartItemId}
                    cartItem={entry}
                    themeColor={themeColor}
                    themeTextColor={themeTextColor}
                    onEdit={onEdit}
                  />
                ))
              ) : (
                <div className="py-8 text-center text-sm text-gray-400">
                  No items in cart yet.
                </div>
              )}
            </div>

            {/* Add new customization */}
            <div className="px-4 py-4 border-t border-gray-100">
              <button
                onClick={() => {
                  onClose();
                  onAddNew(item);
                }}
                className="w-full py-3 rounded-xl text-sm font-black transition-all active:scale-95 flex items-center justify-center gap-2"
                style={{ backgroundColor: `${themeColor}12`, color: themeColor }}
              >
                <span className="text-lg leading-none font-black">+</span>
                Add new customization
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(sheet, document.body);
};

export default ItemRepeatSheet;
